import { useEffect, useState } from 'react'
import { analytics, EVENT_NAMES } from '../services/Analytics/Analytics'

// https://stackoverflow.com/questions/58646758/how-to-detect-darkmode-on-samsung-internet-browser/78808834#78808834
// Detects forced dark mode in browsers that aggressively invert colors by default.
// These browsers IGNORE <meta name="color-scheme" content="light"> and :root { color-scheme: only light; }
// - Samsung Internet: Forces dark mode when system dark mode is enabled (cannot be prevented)
// - MIUI/Xiaomi Browser: System-level forced dark mode on Xiaomi devices (cannot be prevented)
// Note: Edge/Chrome respect color-scheme meta and CSS, so they're prevented by those rules

const SAMSUNG_REGEX = /Samsung/i
const XIAOMI_REGEX = /MIUI|XiaoMi/i
const FORCED_DARK_MODE_REGEX = new RegExp(
  `${SAMSUNG_REGEX.source}|${XIAOMI_REGEX.source}`,
  'i'
)

async function checkIsDarkMode(): Promise<boolean> {
  return new Promise((resolve) => {
    if (
      typeof window !== 'undefined' &&
      window.navigator.userAgent.match(FORCED_DARK_MODE_REGEX)
    ) {
      const img = new Image()
      img.onload = () => {
        const ctx = document.createElement('canvas').getContext('2d')
        if (!ctx) {
          resolve(false)
          return
        }
        ctx.drawImage(img, 0, 0)
        const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
        // eslint-disable-next-line no-bitwise
        const isDarkMode = (r & b & g) < 255
        if (isDarkMode) {
          // Detect which browser triggered the forced dark mode
          const userAgent = window.navigator.userAgent
          let browserType = 'Unknown'
          if (userAgent.match(SAMSUNG_REGEX)) {
            browserType = 'Samsung'
          } else if (userAgent.match(XIAOMI_REGEX)) {
            browserType = 'MIUI'
          }

          analytics.setUserProperties({
            'Forced Dark Mode': true,
            'Dark Mode Browser': browserType,
          })

          analytics.track(EVENT_NAMES.DARK_MODE_DETECTED, {
            'Browser Type': browserType,
            'User Agent': userAgent,
          })
        }
        resolve(isDarkMode)
      }
      img.src =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IndoaXRlIi8+PC9zdmc+'
    } else {
      resolve(false)
    }
  })
}

// Detects browsers that force dark mode and ignore color-scheme meta/CSS prevention.
// This affects color accuracy for photo products, so we alert users to switch to light mode.
// Only detects Samsung Internet and MIUI/Xiaomi (Edge/Chrome are prevented by color-scheme rules)
export function useDetectSamsungDarkMode() {
  const [darkMode, setDarkMode] = useState(false)
  useEffect(() => {
    checkIsDarkMode().then((isDarkMode) => setDarkMode(isDarkMode))
  }, [])

  return darkMode
}
